import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Signal, afterRenderEffect, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { DEFAULT_SEARCH_RADIUS_METERS, PlaceSuggestion, PlacesService, ResolvedPlace, SEARCH_RADII_METERS, SearchArea } from '@lib/guarantees/data-access';
import { selectMemberProfile } from '@lib/members/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, switchMap, take, tap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { OptionListComponent } from '../components/common/option-list.component';
import { OptionRowDirective } from '../components/common/option-row.directive';
import { OVERLAY_POSITIONS, SEARCH_DEBOUNCE_MS, SearchFieldComponent, fieldWidth } from '../components/common/search-field.component';
import { SEGMENTED_GROUP_CLASS, ToggleDirective } from '../components/common/toggle.directive';
import { MapPoint } from './location-map.component';

/** One radius the search may be set to, with the figure the button carries. */
interface RadiusOption {
  meters: number;
  km: number;
}

const RADIUS_OPTIONS: RadiusOption[] = SEARCH_RADII_METERS.map(meters => ({ meters, km: Math.round(meters / 1000) }));

/**
 * Where to search from, and how far — stated in words, over the map that shows it.
 *
 * Every integrated search is narrowed by distance on the other side, and until this control existed
 * none of them said from where: the pharmacy basket was centred on Kyiv whoever the member was, and
 * the two schedules sent no point at all and quietly swept the country.
 *
 * Nothing is searched until the question is answered. The manager arranges care for somebody else,
 * so there is no position to sense and no sensible guess to make on their behalf — a list of the
 * wrong city's clinics, offered before anyone asked for it, is worse than an empty screen.
 *
 * The member's own address is offered as one click rather than applied: it is right most of the
 * time, and a booking made for somebody at work or away from home is exactly the case that a
 * silently assumed centre gets wrong without ever saying so.
 *
 * Once somewhere is named, the field folds to a chip that reads it back. An open search box is an
 * invitation to type into, and the results below carry a box of their own — two of them standing
 * at once look like the same question asked twice, and only one of them would answer.
 *
 * The control states the place and holds no map of its own. The one the caller already shows is
 * where a point is taken from and where the answers appear, so there is only ever the one.
 */
@Component({
  selector: 'guarantees-search-area',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, MatIcon, NgTemplateOutlet, OptionListComponent, OptionRowDirective, SearchFieldComponent, ToggleDirective, TranslatePipe],
  host: { class: 'block' },
  template: `
    <div class="flex flex-col gap-1.5">
      @if (showField()) {
        <div class="flex flex-wrap items-center gap-1.5">
          <span class="min-w-[12rem] flex-1" (keydown.escape)="settle()">
            <guarantees-search-field
              cdkOverlayOrigin
              #origin="cdkOverlayOrigin"
              icon="location_on"
              name="searchArea"
              [ariaLabel]="'guaranteeV2.label.searchArea' | translate"
              [placeholder]="centreLabel() || ('guaranteeV2.message.searchAddress' | translate)"
              [filled]="!!centreLabel()"
              [value]="term()"
              [loading]="loading()"
              (valueChange)="type($event)"
              (opened)="open.set(true)"
              (closed)="settle()"></guarantees-search-field>
          </span>

          <!-- Right most of the time and one click when it is, but never assumed on their behalf. -->
          @if (home(); as address) {
            <button
              class="flex max-w-full shrink items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-0 px-2 py-1 text-sm text-neutral-600 hover:border-primary-300 hover:bg-primary-50/60 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40"
              type="button"
              [disabled]="locatingHome()"
              [title]="address"
              (click)="useHome()">
              <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">home</mat-icon>
              <span class="shrink-0 font-semibold">{{ 'guaranteeV2.action.useMemberAddress' | translate }}</span>
              <span class="min-w-0 truncate">{{ address }}</span>
            </button>
          }
        </div>

        <div class="flex flex-wrap items-center gap-1.5">
          <ng-container *ngTemplateOutlet="radiusGroup"></ng-container>
        </div>

        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="origin"
          [cdkConnectedOverlayOpen]="open() && term().length > 0"
          [cdkConnectedOverlayWidth]="panelWidth()"
          [cdkConnectedOverlayPositions]="overlayPositions"
          (overlayOutsideClick)="open.set(false)">
          <guarantees-option-list
            [loading]="loading()"
            [loadingMessage]="'guaranteeV2.message.searchingAddresses' | translate"
            [empty]="suggestions().length === 0"
            [emptyMessage]="'guaranteeV2.message.noAddresses' | translate">
            @for (suggestion of suggestions(); track suggestion.id) {
              <li>
                <button guaranteesOption type="button" (click)="choose(suggestion)">
                  <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400">location_on</mat-icon>

                  <span class="flex min-w-0 flex-col">
                    <span class="min-w-0 truncate font-semibold text-neutral-700">{{ suggestion.name }}</span>

                    @if (suggestion.context) {
                      <span class="min-w-0 truncate text-neutral-500">{{ suggestion.context }}</span>
                    }
                  </span>
                </button>
              </li>
            }
          </guarantees-option-list>
        </ng-template>
      } @else {
        <!-- The place folded to one line beside its radius. The field is for changing the answer,
             not for reading it back, so it returns only when the chip is asked to. -->
        <div class="flex flex-wrap items-center gap-1.5">
          <button
            class="flex max-w-full min-w-0 items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-0 px-2 py-1 text-sm text-neutral-700 shadow-xs hover:border-primary-300 hover:bg-primary-50/60 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
            type="button"
            [title]="'guaranteeV2.action.changeSearchArea' | translate"
            [attr.aria-label]="('guaranteeV2.action.changeSearchArea' | translate) + ': ' + centreLabel()"
            (click)="edit()">
            <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-primary-600">location_on</mat-icon>
            <span class="min-w-0 truncate font-semibold">{{ centreLabel() }}</span>
            <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400">edit</mat-icon>
          </button>

          <ng-container *ngTemplateOutlet="radiusGroup"></ng-container>
        </div>
      }

      <!-- Said once and quietly: without it the field looks broken rather than unavailable. -->
      @if (lookupFailed()) {
        <p class="flex items-center gap-1 text-xs text-neutral-400">
          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">info</mat-icon>
          {{ 'guaranteeV2.message.addressLookupUnavailable' | translate }}
        </p>
      }
    </div>

    <!-- How far out the partners are worth offering from — the other half of "where", so it stands
         beside the place whichever form the place is taking. -->
    <ng-template #radiusGroup>
      <span class="text-sm text-neutral-500">{{ 'guaranteeV2.label.searchAreaRadius' | translate }}</span>

      <span [class]="segmentedGroup">
        @for (option of radii; track option.meters) {
          <button
            guaranteesToggle
            type="button"
            size="sm"
            shape="segment"
            [pressed]="radius() === option.meters"
            [attr.aria-label]="'guaranteeV2.label.searchRadius' | translate: { km: option.km }"
            (click)="radiusPicked.emit(option.meters)">
            {{ option.km }}
          </button>
        }

        <span class="flex items-center pr-2 pl-1 text-xs font-semibold text-neutral-400">{{ 'guaranteeV2.label.kilometres' | translate }}</span>
      </span>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchAreaComponent {
  /** Where the search stands now, so the field reads it back. Unset until somewhere has been named. */
  area = input<SearchArea | undefined>(undefined);
  /** How far out, held by the caller so a radius chosen before a place is not lost to the order. */
  radius = input.required<number>();

  placed = output<ResolvedPlace>();
  radiusPicked = output<number>();

  protected readonly segmentedGroup = SEGMENTED_GROUP_CLASS;
  protected readonly radii = RADIUS_OPTIONS;
  protected readonly overlayPositions = OVERLAY_POSITIONS;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly places = inject(PlacesService);
  private readonly translate = inject(TranslateService);
  private readonly profile = inject(Store).selectSignal(selectMemberProfile);

  protected readonly term = signal('');
  protected readonly suggestions = signal<PlaceSuggestion[]>([]);
  protected readonly loading = signal(false);
  protected readonly open = signal(false);

  /** The chip has been asked to become the field again, until the next answer or a step away. */
  protected readonly editing = signal(false);

  /** The field is the question being asked; once it is answered, the chip stands in for it. */
  protected readonly showField = computed(() => !this.area() || this.editing());

  /** Set while the member's address is being turned into a point, so the chip cannot be double-clicked. */
  protected readonly locatingHome = signal(false);

  /** Set once an address was asked for and nothing came back, which is a fact about the api. */
  protected readonly lookupFailed = signal(false);

  /**
   * The centre in words. A point taken off the map has no name of its own and is called what it is:
   * the chip would otherwise read as though nothing had been chosen while a circle sat on the map.
   */
  protected readonly centreLabel = computed(() => {
    const area = this.area();

    return area ? area.label || this.translate.instant('guaranteeV2.label.mapPoint') : '';
  });

  /** The member's own address as they gave it, shown on the chip that offers to search around it. */
  protected readonly home = computed(() => describeMemberHome(this.profile()));

  private readonly origin = viewChild('origin', { read: CdkOverlayOrigin });

  /**
   * Measured when the list opens rather than read off the element in the template: a field inside a
   * dialog still being laid out measures zero, which the overlay reads as no width at all.
   */
  protected readonly panelWidth = signal(0);

  constructor() {
    toObservable(this.term)
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap(term => {
          this.loading.set(term.trim().length > 0);

          const origin = this.origin();
          if (origin) {
            this.panelWidth.set(fieldWidth(origin));
          }
        }),
        switchMap(term => this.places.suggest(term)),
        takeUntilDestroyed()
      )
      .subscribe(found => {
        this.suggestions.set(found);
        this.loading.set(false);
        this.lookupFailed.set(found.length === 0 && this.term().trim().length > 2);
      });

    // After render, because the field is not there to focus until the chip's click has put it back.
    afterRenderEffect(() => {
      if (this.editing()) {
        this.host.nativeElement.querySelector('input')?.focus();
      }
    });
  }

  protected type(value: string): void {
    this.term.set(value);
    this.open.set(true);
  }

  /** The chip unfolds back into the field, focused, empty and ready: the place is a fresh question. */
  protected edit(): void {
    this.term.set('');
    this.suggestions.set([]);
    this.editing.set(true);
  }

  /**
   * The field is left without an answer — a blur, or escape — so the chip takes back over where
   * there is one to show. Typing towards nowhere is not kept: the chip already states the answer
   * that still stands.
   */
  protected settle(): void {
    this.open.set(false);
    this.editing.set(false);

    if (this.area()) {
      this.term.set('');
    }
  }

  protected choose(suggestion: PlaceSuggestion): void {
    this.open.set(false);
    this.editing.set(false);
    this.term.set('');
    this.suggestions.set([]);
    this.loading.set(true);

    this.places
      .resolve(suggestion.id)
      .pipe(take(1))
      .subscribe(place => {
        this.loading.set(false);

        // An address taken and then not resolved moves nothing, which is silent unless it is said.
        this.lookupFailed.set(!place);

        if (place) {
          this.placed.emit({ ...place, label: place.label || suggestion.name });
        }
      });
  }

  /**
   * The member's own address, turned into a point only now that it has been asked for.
   *
   * A failure leaves the search where it was rather than falling back to a city nobody named — the
   * map is still there, and works whether or not addresses can be looked up at all.
   */
  protected useHome(): void {
    const address = this.home();

    if (!address) {
      return;
    }

    this.locatingHome.set(true);

    this.places
      .locate(address)
      .pipe(take(1))
      .subscribe(place => {
        this.locatingHome.set(false);
        this.lookupFailed.set(!place);

        if (place) {
          this.editing.set(false);
          this.placed.emit(place);
        }
      });
  }
}

/**
 * Where the member lives, as one line an address lookup can answer.
 *
 * The street alone is ambiguous across a country and the city alone is a whole city, so both are
 * sent where the profile holds both — unless the street already names the city, which is how most
 * of them are typed in.
 */
export function describeMemberHome(profile: { actualAddress?: string; legalAddress?: string; city?: string } | undefined): string {
  const street = (profile?.actualAddress || profile?.legalAddress || '').trim();
  const city = (profile?.city || '').trim();

  if (!street) {
    return city;
  }

  return city && !street.toLowerCase().includes(city.toLowerCase()) ? `${street}, ${city}` : street;
}

/**
 * The area a picker searches over, as the strip above and the map below both read and write it.
 *
 * The centre and the radius are held apart: a radius chosen before a place is named would otherwise
 * be lost to the order they came in, and nothing is an area until both are there.
 */
export interface SearchAreaState {
  /** Where the search is centred and how far out. Unset until somewhere has been named. */
  area: Signal<SearchArea | undefined>;
  radiusMeters: Signal<number>;
  /** Where the map stands before anywhere is named: the member's own streets, at the radius set. */
  preview: Signal<SearchArea | undefined>;
  /** What was answered before the step was left, put back so the question is not asked twice. */
  restore(area: SearchArea): void;
  /**
   * The member's whereabouts, looked up quietly. It frames the first view at the scale the search
   * will run at and asks nothing of any partner — the search still waits for the manager to say where.
   */
  previewHome(): void;
  /** An address named in the strip, or the member's own. */
  place(place: ResolvedPlace): void;
  /**
   * A point taken off the map, which is the answer when the address is one nobody can spell — or
   * when addresses cannot be looked up at all. Left unnamed: turning it back into a street is a
   * second api, and the map it was taken from already shows where it is.
   */
  placePoint(point: MapPoint): void;
  setRadius(meters: number): void;
}

/**
 * The search area as one piece of state, shared by every picker that narrows by distance.
 *
 * Every change to a settled area is handed to the caller, so a step left and returned to can put
 * the same area back rather than ask for it again. Must be called in an injection context.
 */
export function createSearchAreaState(changed: (area: SearchArea) => void): SearchAreaState {
  const places = inject(PlacesService);
  const destroyRef = inject(DestroyRef);
  const profile = inject(Store).selectSignal(selectMemberProfile);

  const centre = signal<ResolvedPlace | undefined>(undefined);
  const radiusMeters = signal(DEFAULT_SEARCH_RADIUS_METERS);
  const previewPoint = signal<ResolvedPlace | undefined>(undefined);

  const area = computed<SearchArea | undefined>(() => {
    const at = centre();

    return at ? { ...at, radiusMeters: radiusMeters() } : undefined;
  });

  const preview = computed<SearchArea | undefined>(() => {
    const at = previewPoint();

    return at ? { latitude: at.latitude, longitude: at.longitude, radiusMeters: radiusMeters() } : undefined;
  });

  const report = (): void => {
    const current = area();

    if (current) {
      changed(current);
    }
  };

  return {
    area,
    radiusMeters,
    preview,
    restore(saved: SearchArea): void {
      centre.set({ latitude: saved.latitude, longitude: saved.longitude, label: saved.label ?? '' });
      radiusMeters.set(saved.radiusMeters);
    },
    previewHome(): void {
      const home = describeMemberHome(profile());

      if (home) {
        places
          .locate(home)
          .pipe(take(1), takeUntilDestroyed(destroyRef))
          .subscribe(place => previewPoint.set(place));
      }
    },
    place(place: ResolvedPlace): void {
      centre.set(place);
      report();
    },
    placePoint(point: MapPoint): void {
      centre.set({ ...point, label: '' });
      report();
    },
    setRadius(meters: number): void {
      radiusMeters.set(meters);

      if (centre()) {
        report();
      }
    },
  };
}
